import React, { useState } from "react";
import { Box, Chip, TextField } from "@mui/material";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "./shared";
import {
  BLANK_NAME_MAX_LENGTH,
  blankNameError,
  templateBlankToken,
  TEMPLATE_BLANK_TYPES,
  type TemplateBlankType,
} from "../domain/notices/templateBlanks";

const CHIP_LABELS: Record<TemplateBlankType, string> = {
  text: "Text",
  date: "Date",
  time: "Time",
  amount: "Amount",
};

const NAME_PLACEHOLDERS: Record<TemplateBlankType, string> = {
  text: "e.g. Guest name",
  date: "e.g. Closing date",
  time: "e.g. Start time",
  amount: "e.g. Trip cost",
};

interface TemplateBlankChipsProps {
  /** Called with the Blank text to insert, e.g. `{% date: Closing date %}`. */
  onInsert: (token: string) => void;
}

/**
 * "+ Text · + Date · + Time · + Amount" chips. Each asks for the Blank's name,
 * then hands back the Blank text for the Template's title or text.
 */
const TemplateBlankChips: React.FC<TemplateBlankChipsProps> = ({ onInsert }) => {
  const [askingFor, setAskingFor] = useState<TemplateBlankType | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string>();

  const ask = (type: TemplateBlankType) => {
    setName("");
    setError(undefined);
    setAskingFor(type);
  };

  const close = () => setAskingFor(null);

  const add = () => {
    if (!askingFor) return;
    const nameError = blankNameError(name);
    if (nameError) {
      setError(nameError);
      return;
    }
    onInsert(templateBlankToken(askingFor, name));
    close();
  };

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {TEMPLATE_BLANK_TYPES.map((type) => (
          <Chip
            key={type}
            label={`+ ${CHIP_LABELS[type]}`}
            variant="outlined"
            size="small"
            onClick={() => ask(type)}
          />
        ))}
      </Box>

      <ActionModal
        open={askingFor !== null}
        onClose={close}
        title={`What's this ${askingFor ? CHIP_LABELS[askingFor].toLowerCase() : ""}?`}
        maxWidth="xs"
        footer={
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
            <NoticeboardButton
              variant="secondary"
              size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
              onClick={close}
            >
              Cancel
            </NoticeboardButton>
            <NoticeboardButton
              variant="primary"
              size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
              onClick={add}
            >
              Add value
            </NoticeboardButton>
          </Box>
        }
      >
        <TextField
          label="Name"
          required
          autoFocus
          fullWidth
          size="small"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(undefined);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          error={!!error}
          helperText={error}
          placeholder={askingFor ? NAME_PLACEHOLDERS[askingFor] : undefined}
          inputProps={{ maxLength: BLANK_NAME_MAX_LENGTH }}
        />
      </ActionModal>
    </>
  );
};

export default TemplateBlankChips;
